import { type ComponentChildren } from 'preact';
import * as stylex from '@stylexjs/stylex';
import { Dot, type DotStatus } from '../Dot/dot';
import { Skeleton } from '../Skeleton/skeleton';
import { styles } from './stat.styles';
interface StatProps {
  value: ComponentChildren;
  label: string;
  dot?: DotStatus;
  /** Semantic alarm tone — tints the VALUE so hot counts read preattentively
   *  (e.g. "2 P1 open" in danger when > 0). */
  tone?: 'danger' | 'warning' | undefined;
  /** Makes the tile actionable (a real <button> with an arrow affordance) —
   *  "click a card to act": the target is where the number gets fixed,
   *  e.g. open incidents → the incident console. */
  onClick?: (() => void) | undefined;
  /** An actionable tile that currently drives something on the page (the SOC's
   *  in-page drill-down): a primary border, and `aria-pressed` so the state is
   *  not colour-only. Ignored without `onClick`. */
  active?: boolean | undefined;
  /** First load: the value is replaced by a skeleton of the same height, so the
   *  strip keeps its shape and never shows a `0` it does not know. */
  loading?: boolean | undefined;
}
export function Stat({
  value,
  label,
  dot,
  tone,
  onClick,
  active,
  loading = false
}: StatProps) {
  const body = <>
      <div {...stylex.props(styles.value)}>
        {loading ? <span {...stylex.props(styles.valueSkeleton)}>
            <Skeleton width="56px" height="22px" />
          </span> : value}
      </div>
      <div {...stylex.props(styles.label)}>
        {dot != null && <Dot status={dot} />}
        {label}
        {onClick != null && <span aria-hidden="true" {...stylex.props(styles.arrow)}>
            →
          </span>}
      </div>
    </>;
  const tones = [tone === 'danger' && styles.toneDanger, tone === 'warning' && styles.toneWarning];
  if (onClick == null) return <div aria-busy={loading || undefined} {...stylex.props(styles.stat, ...tones)}>
        {body}
      </div>;
  return <button type="button" onClick={onClick} aria-pressed={active} aria-busy={loading || undefined} {...stylex.props(styles.stat, styles.action, tone == null && styles.actionHover, ...tones, active === true && styles.active)}>
      {body}
    </button>;
}